import { AfterViewInit, Component, ElementRef, NgZone, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CloudflareTurnstileService } from '@app/finxone-web-frontend/app/lib/services/cloudflare-turnstile/cloudflare-turnstile.service';
import { Subscription, combineLatest, Subject, takeUntil } from 'rxjs';
import { AutoDestroy } from '@finxone-platform/shared/base-types';

@Component({
  selector: 'finxone-web-frontend-cloudflare-turnstile',
  imports: [CommonModule],
  templateUrl: './cloudflare-turnstile.component.html',
  styleUrl: './cloudflare-turnstile.component.scss',
})
export class CloudflareTurnstileComponent implements AfterViewInit {
  @AutoDestroy private destroy$: Subject<void> = new Subject<void>();
  // make ViewChild non-static so it's only queried after the view is initialized
  @ViewChild('turnstileContainer', { static: false })
  container?: ElementRef<HTMLDivElement>;

  private rendered = false;
  public instanceRenderedOnce = false; // keep visible on same page after token issued (made public for template)
  private shouldRenderSubscription: Subscription | null = null;

  constructor(private zone: NgZone, public turnstileService: CloudflareTurnstileService) {}

  ngAfterViewInit(): void {
    // Subscribe to shouldRender$; instance will render once when needed and remain visible on this page once rendered.
    this.shouldRenderSubscription = combineLatest([this.turnstileService.shouldRender$])
      .pipe(takeUntil(this.destroy$))
      // use a synchronous callback and delegate async work to a separate method to satisfy lint rule
      .subscribe(([shouldRender]) => {
        void this.handleShouldRender(shouldRender);
      });
  }

  // Moved async logic out of subscribe to avoid passing an async function directly to subscribe
  private async handleShouldRender(shouldRender: boolean): Promise<void> {
    const siteKey = this.turnstileService.getSiteKey();
    const shouldShow = !!siteKey && (shouldRender || this.instanceRenderedOnce);

    if (!shouldShow) return;
    if (this.rendered) return;

    this.rendered = true;
    this.instanceRenderedOnce = true;

    this.zone.runOutsideAngular(async () => {
      await this.turnstileService.loadTurnstileScript();
      await this.waitForTurnstile();
      await this.waitForContainer(); // ensure ViewChild is available

      if (!this.container?.nativeElement) {
        console.error('Turnstile container not available');
        return;
      }

      // @ts-expect-error Turnstile global injected by external Cloudflare script
      window.turnstile.render(this.container.nativeElement, {
        sitekey: siteKey || '',
        callback: (token: string) => {
          this.turnstileService.setToken(token, 120);
          console.log('Turnstile token:', token);
        },
        'expired-callback': () => {
          console.log('Turnstile expired');
          this.turnstileService.clearToken();
        },
        'error-callback': () => {
          console.error('Turnstile error');
        },
      });
    });
  }

  private waitForTurnstile(): Promise<void> {
    return new Promise((resolve) => {
      const check = () => {
        // @ts-expect-error Turnstile global may be undefined until external script loads
        if (window.turnstile?.render) {
          resolve();
        } else {
          setTimeout(check, 50);
        }
      };
      check();
    });
  }

  private waitForContainer(timeoutMs = 5000): Promise<void> {
    const start = Date.now();
    return new Promise((resolve) => {
      const check = () => {
        if (this.container?.nativeElement) {
          resolve();
        } else if (Date.now() - start > timeoutMs) {
          resolve(); // give up after timeout; caller should guard presence
        } else {
          setTimeout(check, 50);
        }
      };
      check();
    });
  }
}
